import {
  listItemSchema,
  listSchema,
  listWithItemsSchema,
  type CreateListDto,
  type CreateListItemDto,
  type List,
  type ListItem,
  type ListWithItems,
  type ReorderListsDto,
  type TransferMissingItemsDto,
  type UpdateListDto,
  type UpdateListItemDto,
} from '@mrrmlab/shared-types';
import { z } from 'zod';
import type { HttpClient } from './http';

const listsArraySchema = z.array(listSchema);
const fuzzyMatchSuggestionSchema = z.object({
  candidate: z.string(),
  score: z.number(),
});
const parsedItemsSchema = z.array(
  z.object({
    name: z.string(),
    suggestions: z.array(fuzzyMatchSuggestionSchema).optional(),
  }),
);
const parseImageResponseSchema = z.object({
  items: parsedItemsSchema,
  preprocessedImage: z.string(),
});
const parseTextResponseSchema = z.object({
  items: parsedItemsSchema,
});
const debugLineSchema = z.object({
  crop: z.string(),
  text: z.string(),
  confidence: z.number(),
});
const debugParseImageResponseSchema = z.object({
  originalImage: z.string(),
  preprocessedImage: z.string(),
  lines: z.array(debugLineSchema),
});
const trainingStatsSchema = z.object({
  totalSessions: z.number(),
  totalLines: z.number(),
  correctedLines: z.number(),
  lastSessionAt: z.string().nullable(),
  oldestSessionAt: z.string().nullable(),
});

export type FuzzyMatchSuggestion = { candidate: string; score: number };
export type ParsedItem = { name: string; suggestions?: FuzzyMatchSuggestion[] };
export type ParseImageResponse = { items: ParsedItem[]; preprocessedImage: string };
export type ParseTextResponse = { items: ParsedItem[] };
export type DebugLine = { crop: string; text: string; confidence: number };
export type DebugParseImageResponse = {
  originalImage: string;
  preprocessedImage: string;
  lines: DebugLine[];
};
export type SaveTrainingLine = {
  crop: string;
  originalText: string;
  correctedText: string | null;
  confidence: number;
};
export type TrainingStats = z.infer<typeof trainingStatsSchema>;

export class ListsResource {
  readonly items: ListItemsResource;

  constructor(private readonly http: HttpClient) {
    this.items = new ListItemsResource(http);
  }

  list(signal?: AbortSignal): Promise<List[]> {
    return this.http.request({
      method: 'GET',
      path: '/lists',
      responseSchema: listsArraySchema,
      signal,
    });
  }

  get(id: string, signal?: AbortSignal): Promise<ListWithItems> {
    return this.http.request({
      method: 'GET',
      path: `/lists/${encodeURIComponent(id)}`,
      responseSchema: listWithItemsSchema,
      signal,
    });
  }

  create(dto: CreateListDto, signal?: AbortSignal): Promise<List> {
    return this.http.request({
      method: 'POST',
      path: '/lists',
      body: dto,
      responseSchema: listSchema,
      signal,
    });
  }

  update(id: string, dto: UpdateListDto, signal?: AbortSignal): Promise<List> {
    return this.http.request({
      method: 'PATCH',
      path: `/lists/${encodeURIComponent(id)}`,
      body: dto,
      responseSchema: listSchema,
      signal,
    });
  }

  delete(id: string, signal?: AbortSignal): Promise<void> {
    return this.http.request({
      method: 'DELETE',
      path: `/lists/${encodeURIComponent(id)}`,
      expectEmpty: true,
      signal,
    });
  }

  reorder(dto: ReorderListsDto, signal?: AbortSignal): Promise<void> {
    return this.http.request({
      method: 'POST',
      path: '/lists/reorder',
      body: dto,
      responseSchema: z.object({ ok: z.literal(true) }),
      signal,
    }).then(() => undefined);
  }

  parseImage(
    file: Blob | { uri: string; name: string; type: string },
    options?: { model?: string; signal?: AbortSignal },
  ): Promise<ParseImageResponse> {
    const form = new FormData();
    form.append('image', file as unknown as Blob);
    if (options?.model) form.append('model', options.model);
    return this.http.request({
      method: 'POST',
      path: '/lists/parse-image',
      formData: form,
      responseSchema: parseImageResponseSchema,
      signal: options?.signal,
    });
  }

  parseText(
    text: string,
    options?: { signal?: AbortSignal },
  ): Promise<ParseTextResponse> {
    return this.http.request({
      method: 'POST',
      path: '/lists/parse-text',
      body: { text },
      responseSchema: parseTextResponseSchema,
      signal: options?.signal,
    });
  }

  debugParseImage(
    file: Blob | { uri: string; name: string; type: string },
    options?: { model?: string; signal?: AbortSignal },
  ): Promise<DebugParseImageResponse> {
    const form = new FormData();
    form.append('image', file as unknown as Blob);
    if (options?.model) form.append('model', options.model);
    return this.http.request({
      method: 'POST',
      path: '/lists/debug-parse-image',
      formData: form,
      responseSchema: debugParseImageResponseSchema,
      signal: options?.signal,
    });
  }

  saveTraining(
    lines: SaveTrainingLine[],
    options?: { model?: string; signal?: AbortSignal },
  ): Promise<void> {
    return this.http.request({
      method: 'POST',
      path: '/lists/save-training',
      body: { model: options?.model ?? 'easyocr/de', lines },
      expectEmpty: true,
      signal: options?.signal,
    });
  }

  trainingStats(signal?: AbortSignal): Promise<TrainingStats> {
    return this.http.request({
      method: 'GET',
      path: '/lists/ocr/training-stats',
      responseSchema: trainingStatsSchema,
      signal,
    });
  }
}

export class ListItemsResource {
  constructor(private readonly http: HttpClient) {}

  add(listId: string, dto: CreateListItemDto, signal?: AbortSignal): Promise<ListItem> {
    return this.http.request({
      method: 'POST',
      path: `/lists/${encodeURIComponent(listId)}/items`,
      body: dto,
      responseSchema: listItemSchema,
      signal,
    });
  }

  update(
    listId: string,
    itemId: string,
    dto: UpdateListItemDto,
    signal?: AbortSignal,
  ): Promise<ListItem> {
    return this.http.request({
      method: 'PATCH',
      path: `/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}`,
      body: dto,
      responseSchema: listItemSchema,
      signal,
    });
  }

  delete(listId: string, itemId: string, signal?: AbortSignal): Promise<void> {
    return this.http.request({
      method: 'DELETE',
      path: `/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}`,
      expectEmpty: true,
      signal,
    });
  }

  transferMissing(
    listId: string,
    dto: TransferMissingItemsDto,
    signal?: AbortSignal,
  ): Promise<ListWithItems> {
    return this.http.request({
      method: 'POST',
      path: `/lists/${encodeURIComponent(listId)}/items/transfer-missing`,
      body: dto,
      responseSchema: listWithItemsSchema,
      signal,
    });
  }
}
